import projectStore from '@/store/project'
import {
  bulkActivateExternalBarcodes,
  getBarcode,
  getBarcodeByPath,
  getClientUsers,
  getChildren,
  linkingBarcodes,
  pairBarcode,
  recycleMultipleBarcode,
  tracking,
  undoTracking,
  unpairBarcode,
} from '@/utils/api'
import { DEFAULT_ACTIVATION_DATA } from '@/utils/constants'
import { ELinkType } from '@/utils/enums'
import { addLabelToBarcode } from '@/utils/helpers'
import isEmpty from 'lodash/isEmpty'
import { getCompiledBarcode } from 'smartbarcode-web-core/src/utils/barcodeHelper'
import {
  IActivationData,
  IBarcode,
  IBarcodeSavedImageStatus,
  IBarcodeSearchForm,
  IClientInfo,
  IDisplayBarcodes,
  IProject,
  ITrackingRequestBody,
} from 'smartbarcode-web-core/src/utils/types/index'
import {
  ADD_TRACKING,
  BARCODE_ACTION_COMPLETE,
  BARCODE_SELECTED_TYPE,
  BE_LINKED,
  BULK_ACTIVATE_EXTERNAL,
  CLEAR_BARCODE,
  CLEAR_BULK_ACTIVATION_CONTEXT,
  CLEAR_ERROR,
  CLEAR_NOTIFICATION,
  CLEAR_SCANNED_UPLOAD_STATUS,
  ERROR_OCCUR,
  FETCH_BARCODE,
  FETCH_CLIENT_USERS,
  FETCH_CHILDREN,
  FETCH_NOTIFICATION,
  FETCH_PROJECT_BY_ID,
  LINK_TO,
  NUMBER_BARCODE_ACTION_COMPLETE,
  PAIRING,
  RECYCLE_MULTIPLE_BARCODE,
  RESET_ALL_BARCODE_BLOB,
  SAVE_BARCODE,
  SAVE_CLIENT_USERS,
  SAVE_BARCODE_SEARCH_PARAMS,
  SAVE_CHILDREN,
  SAVE_SCANNED_BARCODES,
  SAVE_SCANNED_EXTERNAL_ID,
  SET_BARCODE_FORM_STEP,
  SET_BULK_ACTIVATION_CONTEXT,
  UNDO_TRACKING,
  UNPAIR,
  UNPAIRING,
  UPDATE_ACTIVATION_DATA_BARCODE,
  UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT,
  UPDATE_SCANNED_RESULT,
  UPDATE_SCANNED_UPLOAD_STATUS,
} from './actions'

const enum EActionCompleted {
  ACTIVATED = 'activated',
  TRACKED = 'tracked',
  RECYCLED = 'recycled',
  PAIRED = 'paired',
  UNPAIRED = 'unpaired',
  UNDO_TRACKING = 'undo_tracking',
  LINKED = 'linked',
  EMPTY = '',
}

interface IBulkActivationContext {
  externalIds: string[]
  returnRoute: string
  operation: string
  extParams: Record<string, unknown>
  rescanRoute?: string
}

interface IDefaultActionParams {
  commit: Function
  dispatch: Function
  state: TBarcodeState
}
interface TBarcodeState {
  barcode?: IBarcode | null
  clientUsers: IClientInfo[]
  children: IDisplayBarcodes[]
  error: boolean
  errorMsg: string | number
  actionCompleted: EActionCompleted
  numberOfBCAffected: number
  currentSelectedBarcodeType?: string
  trackedStep: number
  scannedBCPool: IBarcode[]
  scannedBarcodeBlob: Blob[]
  customFieldsOcrImagesBlobs: Record<string, Blob[]>
  scannedBarcodeStatus?: Record<string, IBarcodeSavedImageStatus>
  barcodeSearchParams: {
    condition?: IBarcodeSearchForm
  }
  scannedExternalId: string
  bulkActivationContext?: IBulkActivationContext | null
}

export default {
  state: {
    barcode: null,
    clientUsers: [],
    children: [],
    error: false,
    errorMsg: '',
    actionCompleted: '',
    numberOfBCAffected: 0,
    currentSelectedBarcodeType: '',
    trackedStep: 0,
    scannedBCPool: [],
    scannedBarcodeBlob: [],
    customFieldsOcrImagesBlobs: {},
    scannedBarcodeStatus: false,
    barcodeSearchParams: {},
    scannedExternalId: '',
    bulkActivationContext: null,
  },
  getters: {
    scannedBarcodeStatus: (state: TBarcodeState) => {
      return state.scannedBarcodeStatus
    },
    scannedExternalId: (state: TBarcodeState) => {
      return state.scannedExternalId
    },
    scannedBarcodeBlob: (state: TBarcodeState) => {
      return state.scannedBarcodeBlob
    },
    customFieldsOcrImagesBlobs: (state: TBarcodeState) => {
      return state.customFieldsOcrImagesBlobs
    },
    barcodeExist: (state: TBarcodeState) => {
      return state.barcode !== null
    },
    barcodeType: (state: TBarcodeState) => {
      return state.barcode?.barcodeType
    },
    barcodeActivated: (state: TBarcodeState) => {
      return !!state.barcode?.isActivated
    },
    trackingNumber: (state: TBarcodeState) => {
      return state.barcode?.activationData?.trackingNumber
    },
    externalId: (state: TBarcodeState) => {
      return state.barcode?.activationData?.externalId
    },
    dimension: (state: TBarcodeState) => {
      return state.barcode?.activationData?.dimension
    },
    origin: (state: TBarcodeState) => {
      return state.barcode?.activationData?.origin
    },
    destination: (state: TBarcodeState) => {
      return state.barcode?.activationData?.destination
    },
    searchCondition: (state: TBarcodeState) => {
      return state.barcodeSearchParams
    },
    currentTrackingPoint: (state: TBarcodeState) => {
      return state.barcode?.currentTrackPointKey
    },
  },
  mutations: {
    [SAVE_CLIENT_USERS]: (state: TBarcodeState, payload: IClientInfo[]) => {
      state.clientUsers = payload ?? []
    },
    [SAVE_CHILDREN]: (state: TBarcodeState, payload: IDisplayBarcodes[]) => {
      state.children = payload
    },
    [SAVE_BARCODE]: (state: TBarcodeState, payload?: IBarcode) => {
      if (payload) {
        payload.activationData = { ...(DEFAULT_ACTIVATION_DATA as IActivationData), ...payload.activationData }
        state.barcode = { ...payload }
        state.error = false
      } else {
        state.barcode = null
        state.error = true
      }
    },
    [UPDATE_ACTIVATION_DATA_BARCODE]: (state: TBarcodeState, payload?: IActivationData) => {
      if (payload && state.barcode) {
        state.barcode.activationData = { ...state.barcode?.activationData, ...payload }
      }
    },
    [UPDATE_SCANNED_RESULT]: (state: TBarcodeState, payload?: Blob[]) => {
      if (payload && state) {
        state.scannedBarcodeBlob = [...payload]
      }
    },
    [UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT]: (state: TBarcodeState, payload?: Record<string, Blob[]>) => {
      if (payload && state) {
        Object.keys(payload).forEach((key) => {
          const blobs = payload[key]
          if (key in state.customFieldsOcrImagesBlobs) {
            state.customFieldsOcrImagesBlobs = {
              ...state.customFieldsOcrImagesBlobs,
              [key]: [...state.customFieldsOcrImagesBlobs[key], ...blobs],
            }
          } else {
            state.customFieldsOcrImagesBlobs = {
              ...state.customFieldsOcrImagesBlobs,
              [key]: blobs,
            }
          }
        })
      }
    },
    [RESET_ALL_BARCODE_BLOB]: (state: TBarcodeState) => {
      state.scannedBarcodeBlob.length = 0
      state.customFieldsOcrImagesBlobs = {}
    },
    [SET_BARCODE_FORM_STEP]: (state: TBarcodeState, step: number) => {
      state.trackedStep = step
    },
    [CLEAR_BARCODE]: (state: TBarcodeState) => {
      state.barcode = null
      state.scannedBarcodeBlob.length = 0
    },
    [BARCODE_ACTION_COMPLETE]: (state: TBarcodeState, action: EActionCompleted = EActionCompleted.EMPTY) => {
      state.actionCompleted = action
    },
    [NUMBER_BARCODE_ACTION_COMPLETE]: (state: TBarcodeState, numberOfBC: number) => {
      state.numberOfBCAffected = numberOfBC
    },
    [BARCODE_SELECTED_TYPE]: (state: TBarcodeState, payload: string) => {
      state.currentSelectedBarcodeType = payload
    },
    [UPDATE_SCANNED_UPLOAD_STATUS]: (state: TBarcodeState, status: Record<string, IBarcodeSavedImageStatus>) => {
      state.scannedBarcodeStatus = { ...state.scannedBarcodeStatus, ...status }
    },
    [CLEAR_SCANNED_UPLOAD_STATUS]: (state: TBarcodeState) => {
      state.scannedBarcodeStatus = {}
    },
    [SAVE_SCANNED_BARCODES]: (state: TBarcodeState, barcodes: IBarcode[]) => {
      state.scannedBCPool = [...barcodes]
    },
    [SAVE_BARCODE_SEARCH_PARAMS]: (state: TBarcodeState, searchParams: { condition: IBarcodeSearchForm }) => {
      state.barcodeSearchParams = { ...searchParams }
    },
    [ERROR_OCCUR]: (state: TBarcodeState, errorMsg: string | number) => {
      state.error = true
      state.errorMsg = errorMsg
    },
    [CLEAR_ERROR]: (state: TBarcodeState) => {
      state.error = false
      state.errorMsg = ''
    },
    [SAVE_SCANNED_EXTERNAL_ID]: (state: TBarcodeState, payload: string) => {
      if (payload) {
        state.scannedExternalId = payload
      } else {
        state.scannedExternalId = ''
      }
    },
    [SET_BULK_ACTIVATION_CONTEXT]: (state: TBarcodeState, payload: IBulkActivationContext) => {
      state.bulkActivationContext = payload
    },
    [CLEAR_BULK_ACTIVATION_CONTEXT]: (state: TBarcodeState) => {
      state.bulkActivationContext = null
    },
  },
  actions: {
    [FETCH_CLIENT_USERS]: async ({ commit }: IDefaultActionParams, clientIds: string[]) => {
      const clients = await getClientUsers(clientIds)
      commit(SAVE_CLIENT_USERS, clients)
    },
    [CLEAR_BARCODE]: ({ commit }: { commit: Function }) => {
      commit(CLEAR_BARCODE)
      commit(CLEAR_NOTIFICATION)
    },
    [FETCH_BARCODE]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
      },
      data: {
        id: string
        path?: string
        externalId?: string
        trackingNumber?: string
      }
    ) => {
      try {
        commit(CLEAR_NOTIFICATION)
        let result: IBarcode

        if (data.path) {
          result = await getBarcodeByPath(data.id, data.path)
        } else {
          result = await getBarcode(data.id, data.externalId, data.trackingNumber)
        }
        const project = projectStore.state.details as IProject

        // Check current project and the barcode ID. If not matched refetch the project.
        if (result.projectId !== project.id) {
          await dispatch(FETCH_PROJECT_BY_ID, result?.projectId)
          if (isEmpty(project)) {
            throw Error('Project setting not found')
          }
        }
        getCompiledBarcode(result, project)
        commit(SAVE_BARCODE, result)
        dispatch(FETCH_NOTIFICATION, {
          barcodeId: data.id,
        })
      } catch (error) {
        // if no, show no found
        commit(SAVE_BARCODE)
      }
    },
    [FETCH_CHILDREN]: async (
      {
        commit,
      }: {
        commit: Function
      },
      barcodeId: string
    ) => {
      const result: IDisplayBarcodes[] = await getChildren(barcodeId)
      const project = projectStore.state.details as IProject
      addLabelToBarcode(result, project)
      commit(SAVE_CHILDREN, result)
    },
    [UNPAIR]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      if (state.barcode?.id) {
        const result = await unpairBarcode({
          parentBarcodeId: state.barcode?.id,
          isUnpairAll: true,
          unpairChildrenIds: [],
        })
        commit(SAVE_BARCODE, result)
        dispatch(NUMBER_BARCODE_ACTION_COMPLETE, 1)
        dispatch(BARCODE_ACTION_COMPLETE, 'unpaired')
      }
    },

    [BARCODE_ACTION_COMPLETE]: (
      {
        commit,
      }: {
        commit: Function
      },
      action = EActionCompleted.EMPTY
    ) => {
      commit(BARCODE_ACTION_COMPLETE, action)
    },
    [NUMBER_BARCODE_ACTION_COMPLETE]: (
      {
        commit,
      }: {
        commit: Function
      },
      numberOfBC: number
    ) => {
      commit(NUMBER_BARCODE_ACTION_COMPLETE, numberOfBC)
    },
    [UNDO_TRACKING]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      payload: {
        barcodeId: string
      }
    ) => {
      await undoTracking(payload.barcodeId)
      const barcode = await getBarcode(payload.barcodeId)
      commit(SAVE_BARCODE, barcode)
      dispatch(NUMBER_BARCODE_ACTION_COMPLETE, 1)
      dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.UNDO_TRACKING)
    },

    [SAVE_SCANNED_BARCODES]: async (
      {
        commit,
      }: {
        commit: Function
      },
      barcodes: IBarcode[]
    ) => {
      commit(SAVE_SCANNED_BARCODES, barcodes)
    },

    [ADD_TRACKING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      await tracking({
        barcodeIds: [id, ...barcodes.map((bc) => bc.id)],
        ...extParams,
      } as ITrackingRequestBody)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length + 1)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.TRACKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [LINK_TO]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        barcodeId: id,
        targetBarcodeIds: barcodes.filter((bc) => bc.id !== id).map((bc) => bc.id),
      }

      await linkingBarcodes(ELinkType.LINK_TO, data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.LINKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [BE_LINKED]: async ({ commit, dispatch, state }: IDefaultActionParams) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        barcodeId: id,
        targetBarcodeIds: barcodes.filter((bc) => bc.id !== id).map((bc) => bc.id),
      }

      await linkingBarcodes(ELinkType.BE_LINKED, data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.LINKED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [PAIRING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        parentBarcodeId: id as string,
        childrenBarcodeIds: barcodes.map((bc) => bc.id),
        ...extParams,
      }
      await pairBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.PAIRED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [UNPAIRING]: async (
      {
        commit,
        dispatch,
        state,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      extParams: Record<string, unknown>
    ) => {
      const id = state.barcode?.id
      const barcodes = state.scannedBCPool
      if (!id || isEmpty(barcodes)) return

      const data = {
        parentBarcodeId: id as string,
        isUnpairAll: false,

        unpairChildrenIds: barcodes.map((bc) => bc.id),
        isDryRun: false,
        ...extParams,
      }

      await unpairBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodes.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.UNPAIRED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [BULK_ACTIVATE_EXTERNAL]: async (
      {
        commit,
        state,
      }: {
        commit: Function
        state: TBarcodeState
      },
      payload: {
        activationData: IActivationData
        barcodeType: string
      }
    ) => {
      const bulkContext = state.bulkActivationContext
      if (!bulkContext) return

      const project = projectStore.state.details as IProject
      const projectId = project?.id
      if (!projectId) return

      const data = {
        externalIds: bulkContext.externalIds,
        projectId,
        activationData: payload.activationData,
        barcodeType: payload.barcodeType,
      }
      await bulkActivateExternalBarcodes(data)
        .then((result) => {
          const activatedBarcodes = result?.barcodes ?? []
          const activatedByExternalId = new Map(
            activatedBarcodes.map((bc: IBarcode) => [bc.activationData?.externalId, bc])
          )
          const updatedPool = state.scannedBCPool.map((bc: IBarcode) =>
            bc.isExternal ? activatedByExternalId.get(bc.id) ?? bc : bc
          )
          commit(SAVE_SCANNED_BARCODES, updatedPool)
          commit(CLEAR_BULK_ACTIVATION_CONTEXT)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [SAVE_SCANNED_EXTERNAL_ID]: (
      {
        commit,
      }: {
        commit: Function
      },
      externalId: string
    ) => {
      commit(SAVE_SCANNED_EXTERNAL_ID, externalId)
    },

    [UPDATE_SCANNED_RESULT]: (
      {
        commit,
      }: {
        commit: Function
      },
      listBlob?: Blob[]
    ) => {
      commit(UPDATE_SCANNED_RESULT, listBlob)
    },

    [UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT]: (
      {
        commit,
      }: {
        commit: Function
      },
      customFieldsOcrImagesBlobs?: Record<string, Blob[]>
    ) => {
      commit(UPDATE_CUSTOM_FIELD_OCR_IMAGE_RESULT, customFieldsOcrImagesBlobs)
    },

    [RECYCLE_MULTIPLE_BARCODE]: async (
      {
        commit,
        dispatch,
      }: {
        commit: Function
        dispatch: Function
        state: TBarcodeState
      },
      barcodeIds: string[]
    ) => {
      if (isEmpty(barcodeIds)) return
      const data = {
        isDryRun: false,
        barcodeIds: barcodeIds,
      }
      await recycleMultipleBarcode(data)
        .then(() => {
          dispatch(NUMBER_BARCODE_ACTION_COMPLETE, barcodeIds.length)
          dispatch(BARCODE_ACTION_COMPLETE, EActionCompleted.RECYCLED)
        })
        .catch((error) => commit(ERROR_OCCUR, error))
    },
    [SAVE_BARCODE]: async (
      {
        commit,
      }: {
        commit: Function
      },
      payload: IBarcode
    ) => {
      commit(SAVE_BARCODE, payload)
    },
    [SET_BULK_ACTIVATION_CONTEXT]: (
      {
        commit,
      }: {
        commit: Function
      },
      payload: IBulkActivationContext
    ) => {
      commit(SET_BULK_ACTIVATION_CONTEXT, payload)
    },
    [CLEAR_BULK_ACTIVATION_CONTEXT]: ({ commit }: { commit: Function }) => {
      commit(CLEAR_BULK_ACTIVATION_CONTEXT)
    },
  },
}
